import {
  AdminApiError,
  type AdminClient,
  type IntegratorSummary,
} from '@apx/admin-sdk';
import { io, type Socket } from 'socket.io-client';
import type { RoundEventFrame } from '@apx/protocol';
import { fmtTime } from '../format.js';

export interface InspectorAllPageDeps {
  client: AdminClient;
  gameId: string;
  gatewayUrl: string;
  roundEngineBaseUrl: string;
}

// Frame shape comes from @apx/protocol (RoundEventFrame). Declaring it locally is
// how this file ended up reading `payload.amount` — a key the engine stopped
// publishing — leaving the per-slot bars silently empty.
type RoundEvent = RoundEventFrame;

interface TenantCard {
  integrator: IntegratorSummary;
  socket: Socket | null;
  events: RoundEvent[];
  activeRoundId: string | null;
  activeRoundState: string | null;
  totals: Record<string, string>;
  el: HTMLElement;
  destroyed: boolean;
}

const MAX_EVENTS_PER_CARD = 8;

/**
 * Multi-integrator inspector — one card per active integrator. Each card opens its
 * own gateway WS using a integrator-scoped inspector JWT, subscribes to the
 * `game:<gameId>:integrator:<tid>` room, and renders live state pill + last 3
 * events + per-slot mini-bars. Click a card to drill into the single-integrator
 * inspector with that integrator selected.
 */
export async function renderInspectorAllPage(
  root: HTMLElement,
  deps: InspectorAllPageDeps,
): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">Live Inspector — all integrators</h1>
    <p class="page-sub">
      One card per active integrator. Each opens its own read-only WS to the
      gateway and streams live round events. Click a card to drill in.
    </p>
    <div id="ia-grid" style="display:grid;grid-template-columns:repeat(auto-fill,minmax(340px,1fr));gap:14px"></div>
  `;
  const gridEl = root.querySelector<HTMLElement>('#ia-grid')!;

  let tenants: IntegratorSummary[];
  try {
    tenants = await deps.client.listIntegrators();
  } catch (err) {
    gridEl.innerHTML = `<p class="error">${escapeHtml(describe(err))}</p>`;
    return;
  }

  const activeTenants = tenants.filter((t) => t.status === 'active');
  if (activeTenants.length === 0) {
    gridEl.innerHTML = '<p class="muted">No active integrators.</p>';
    return;
  }

  const cards: TenantCard[] = [];
  for (const t of activeTenants) {
    const cardEl = document.createElement('div');
    cardEl.className = 'card';
    cardEl.style.margin = '0';
    cardEl.style.cursor = 'pointer';
    cardEl.style.transition = 'border-color 0.12s ease, transform 0.12s ease';
    cardEl.addEventListener('mouseenter', () => {
      cardEl.style.borderColor = 'var(--accent-2)';
      cardEl.style.transform = 'translateY(-1px)';
    });
    cardEl.addEventListener('mouseleave', () => {
      cardEl.style.borderColor = '';
      cardEl.style.transform = '';
    });
    cardEl.addEventListener('click', () => {
      localStorage.setItem('apx-admin:integrator', t.id);
      window.location.href = `/games/${deps.gameId}/inspector`;
    });
    const card: TenantCard = {
      integrator: t,
      socket: null,
      events: [],
      activeRoundId: null,
      activeRoundState: null,
      totals: {},
      el: cardEl,
      destroyed: false,
    };
    cards.push(card);
    gridEl.appendChild(cardEl);
    renderCard(card);
    void attachSocket(card, deps);
  }

  // Initial state fetch once per card; everything after flows off the
  // WS feed (round.opened / round.state_change / round.settled).
  for (const card of cards) void refreshActiveRound(card, deps);

  // Tear down sockets when the page is removed.
  const observer = new MutationObserver(() => {
    if (!document.body.contains(root)) {
      for (const card of cards) {
        card.destroyed = true;
        card.socket?.disconnect();
      }
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

async function attachSocket(card: TenantCard, deps: InspectorAllPageDeps): Promise<void> {
  let token: string;
  try {
    const r = await deps.client.inspectorToken(card.integrator.id);
    token = r.token;
  } catch (err) {
    console.warn(`inspector token for ${card.integrator.slug} failed`, err);
    return;
  }
  if (card.destroyed) return;
  const socket = io(deps.gatewayUrl, {
    auth: { token },
    transports: ['websocket'],
    reconnection: true,
  });
  card.socket = socket;
  socket.on('connect', () => {
    socket.emit('round.subscribe', { gameId: deps.gameId, integratorId: card.integrator.id });
    renderCard(card);
  });
  socket.on('round.event', (event: RoundEvent) => {
    // Same cross-game leak as inspector.ts: the gateway joins this socket to the
    // base room of every game enabled for the integrator, so frames for games
    // this page isn't rendering arrive here and would hijack activeRoundId.
    if (event.gameId !== deps.gameId) return;
    card.events.unshift(event);
    if (card.events.length > MAX_EVENTS_PER_CARD) card.events.length = MAX_EVENTS_PER_CARD;
    if (event.type === 'round.opened') {
      card.activeRoundId = event.roundId;
      card.activeRoundState = 'betting_open';
      card.totals = {};
    } else if (event.type === 'round.settled') {
      card.activeRoundState = 'settled';
    } else if (event.type === 'bet.placed') {
      const slot = event.payload.betType;
      // `amount` is the pre-rename key — older engine builds still emit it.
      const amt = event.payload.creditAmount ?? event.payload.amount;
      if (slot && amt) {
        const prev = BigInt(card.totals[slot] ?? '0');
        card.totals[slot] = (prev + BigInt(amt)).toString();
      }
    }
    renderCard(card);
  });
  socket.on('disconnect', () => renderCard(card));
  socket.on('connect_error', () => renderCard(card));
}

async function refreshActiveRound(card: TenantCard, deps: InspectorAllPageDeps): Promise<void> {
  try {
    const url = new URL(`${deps.roundEngineBaseUrl}/v1/rounds/active`);
    url.searchParams.set('gameId', deps.gameId);
    url.searchParams.set('integratorId', card.integrator.id);
    const res = await fetch(url, { method: 'GET' });
    if (!res.ok) return;
    const round = (await res.json()) as
      | { id: string; state: string; totalBet: string; participantCount: number }
      | null;
    if (!round) return;
    card.activeRoundId = round.id;
    card.activeRoundState = round.state;
    renderCard(card);
  } catch {
    /* network blip — skip */
  }
}

function renderCard(card: TenantCard): void {
  const t = card.integrator;
  const connected = card.socket?.connected === true;
  const pillKind = connected ? 'live' : 'warn';
  const eventRows = card.events
    .slice(0, 3)
    .map((e) => {
      const ts = fmtTime(e.ts);
      return `<div style="font-size:11px;color:var(--text-dim);display:flex;gap:8px;padding:2px 0">
        <span style="min-width:60px;font-variant-numeric:tabular-nums">${ts}</span>
        <code style="color:var(--accent-2)">${escapeHtml(e.type)}</code>
      </div>`;
    })
    .join('');
  const totalsEntries = Object.entries(card.totals).sort((a, b) => Number(b[1]) - Number(a[1])).slice(0, 5);
  const maxTotal =
    totalsEntries.length > 0 ? Math.max(...totalsEntries.map(([, v]) => Number(v))) : 0;
  const totalsRows = totalsEntries
    .map(([slot, v]) => {
      const pct = maxTotal > 0 ? Math.round((Number(v) / maxTotal) * 100) : 0;
      return `<div style="display:flex;align-items:center;gap:8px;font-size:11px;padding:1px 0">
        <span style="min-width:64px"><code>${escapeHtml(slot)}</code></span>
        <div style="flex:1;height:6px;background:var(--panel-2);border-radius:3px;overflow:hidden">
          <div style="width:${pct}%;height:100%;background:var(--accent-2)"></div>
        </div>
        <span style="min-width:60px;text-align:right;font-variant-numeric:tabular-nums">${Number(v).toLocaleString()}</span>
      </div>`;
    })
    .join('');

  card.el.innerHTML = `
    <div style="display:flex;align-items:baseline;justify-content:space-between;margin-bottom:6px">
      <h2 style="margin:0;font-size:15px">${escapeHtml(t.name)}</h2>
      <span class="pill ${pillKind}" style="font-size:10px">${connected ? 'live' : 'offline'}</span>
    </div>
    <div style="font-size:11px;color:var(--text-dim);margin-bottom:10px">
      <code>${escapeHtml(t.slug)}</code> · ${escapeHtml(t.id.slice(0, 8))}…
    </div>

    <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;font-size:12px">
      <span class="muted">Round:</span>
      ${card.activeRoundId ? `<code style="font-size:11px">${escapeHtml(card.activeRoundId.slice(-12))}</code>` : '<span class="muted">—</span>'}
      ${card.activeRoundState ? `<span class="pill ${card.activeRoundState === 'betting_open' ? 'live' : ''}" style="font-size:10px">${escapeHtml(card.activeRoundState)}</span>` : ''}
    </div>

    ${
      totalsRows
        ? `<div style="margin:8px 0">${totalsRows}</div>`
        : '<div class="muted" style="font-size:11px;margin:8px 0">No bets yet for this round.</div>'
    }

    <div style="border-top:1px solid var(--border);margin-top:8px;padding-top:8px">
      ${eventRows || '<div class="muted" style="font-size:11px">Waiting for events…</div>'}
    </div>
  `;
}

function describe(err: unknown): string {
  if (err instanceof AdminApiError) return `${err.code}: ${err.message}`;
  return err instanceof Error ? err.message : String(err);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return c;
    }
  });
}
